/** Credit balance, packs, card checkout (Polar), VietQR bank transfer (SePay), and credit history hooks */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, ApiError, fetchBlob } from '../lib/api-client'

export type CreditBalance = {
  purchasedCredits: number
  freeCredits: number
  totalCredits: number
}

export type CreditPack = {
  id: string
  name: string
  credits: number
  bonusCredits: number
  priceCents: number
  /** VND price for bank transfer; null when the pack is not sold by transfer */
  priceVnd: number | null
  sortOrder: number
}

export type CheckoutResponse = {
  checkoutUrl: string
}

export type PaymentsConfig = {
  environment: 'sandbox' | 'production'
  /** Configured ways to pay (absent from older APIs: treat as card only) */
  methods?: { polar: boolean; sepay: boolean }
}

export function usePaymentsConfig() {
  return useQuery<PaymentsConfig, ApiError>({
    queryKey: ['payments', 'config'],
    queryFn: () => apiClient.get<PaymentsConfig>('/api/payments/config'),
    staleTime: 1000 * 60 * 60,
  })
}

export function useCreditBalance() {
  return useQuery<CreditBalance, ApiError>({
    queryKey: ['credits', 'balance'],
    queryFn: () => apiClient.get<CreditBalance>('/api/credits/balance'),
    staleTime: 1000 * 30,
  })
}

export function useCreditPacks() {
  return useQuery<CreditPack[], ApiError>({
    queryKey: ['credits', 'packs'],
    queryFn: () => apiClient.get<CreditPack[]>('/api/credits/packs'),
    staleTime: 1000 * 60 * 10,
  })
}

export function useCreateCheckout() {
  const qc = useQueryClient()
  return useMutation<CheckoutResponse, ApiError, { packId: string }>({
    mutationFn: (input) => apiClient.post<CheckoutResponse>('/api/payments/checkout', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credits', 'balance'] })
    },
  })
}

/** Page envelope returned by the API's paginate() helper */
export type Paginated<T> = {
  data: T[]
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

/** Ledger types written by the API credit service */
export const CREDIT_TRANSACTION_TYPES = ['deduct', 'purchase', 'free_grant', 'refund', 'expire'] as const
export type CreditTransactionType = (typeof CREDIT_TRANSACTION_TYPES)[number]

export type CreditTransaction = {
  id: string
  type: string
  /** Positive = credits added, negative = credits spent */
  amount: number
  /** -1 when unknown (refund rows) */
  balanceAfter: number
  description: string | null
  createdAt: string
  /** Project the credits were spent on (null for other work and for rows from before attribution) */
  projectId?: string | null
  projectName?: string | null
  /** Set when an API key made the request */
  apiKeyId?: string | null
  apiKeyName?: string | null
  userName?: string | null
  userEmail?: string | null
}

/** Ledger filters; `from` / `to` are inclusive ms timestamps */
export type CreditHistoryFilters = {
  type?: CreditTransactionType
  projectId?: string
  from?: number
  to?: number
}

function historyParams(filters: CreditHistoryFilters): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.type) params.set('type', filters.type)
  if (filters.projectId) params.set('projectId', filters.projectId)
  if (filters.from !== undefined) params.set('from', String(filters.from))
  if (filters.to !== undefined) params.set('to', String(filters.to))
  return params
}

export type CreditPurchase = {
  id: string
  packId: string | null
  provider: string
  packName: string
  creditsGranted: number
  /** Minor units for USD (cents); whole units for VND */
  amountPaid: number
  currency: string
  status: string
  createdAt: string
}

const HISTORY_PAGE_SIZE = 20

function nextOffset<T>(last: Paginated<T>): number | undefined {
  return last.hasMore ? last.offset + last.limit : undefined
}

/** Paginated credit ledger, newest first, narrowed by type, project, and date range */
export function useCreditTransactions(filters: CreditHistoryFilters = {}) {
  return useInfiniteQuery<Paginated<CreditTransaction>, ApiError>({
    queryKey: ['credits', 'transactions', filters.type ?? 'all', filters.projectId ?? 'all', filters.from ?? null, filters.to ?? null],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const params = historyParams(filters)
      params.set('limit', String(HISTORY_PAGE_SIZE))
      params.set('offset', String(pageParam))
      return apiClient.get<Paginated<CreditTransaction>>(`/api/credits/transactions?${params}`)
    },
    getNextPageParam: nextOffset,
    staleTime: 1000 * 30,
  })
}

/** Paginated credit pack purchases, newest first */
export function useCreditPurchases() {
  return useInfiniteQuery<Paginated<CreditPurchase>, ApiError>({
    queryKey: ['credits', 'purchases'],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      apiClient.get<Paginated<CreditPurchase>>(`/api/credits/purchases?limit=${HISTORY_PAGE_SIZE}&offset=${String(pageParam)}`),
    getNextPageParam: nextOffset,
    staleTime: 1000 * 30,
  })
}

/** Download the filtered ledger as CSV (the API caps one export at 10,000 rows) */
export async function downloadCreditHistoryCsv(filters: CreditHistoryFilters): Promise<void> {
  const query = historyParams(filters).toString()
  const { blob, filename } = await fetchBlob(`/api/credits/transactions.csv${query ? `?${query}` : ''}`)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename ?? 'credit-history.csv'
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoke after the click has been handled
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

// ─── SePay VietQR bank transfer ─────────────────────────────────────────

export type SepayOrderStatus = 'pending' | 'paid' | 'expired'

export type SepayOrder = {
  id: string
  packId: string
  /** Payment reference the payer puts in the transfer content */
  code: string
  amountVnd: number
  status: SepayOrderStatus
  createdAt: number
  expiresAt: number
  paidAt: number | null
}

export type SepayOrderResponse = {
  order: SepayOrder
  bank: { accountNumber: string; bankCode: string; accountName: string }
  qrUrl: string
  packName: string
  credits: number
}

/** How often an open transfer dialog asks whether the money arrived */
export const SEPAY_POLL_MS = 4000

export function useCreateSepayOrder() {
  return useMutation<SepayOrderResponse, ApiError, { packId: string }>({
    mutationFn: (input) => apiClient.post<SepayOrderResponse>('/api/payments/sepay/orders', input),
  })
}

/** Order status, polled while it is pending and the tab is visible; on paid the balance and history are refreshed */
export function useSepayOrderStatus(orderId: string | undefined) {
  const qc = useQueryClient()
  return useQuery<SepayOrder, ApiError>({
    queryKey: ['payments', 'sepay', 'order', orderId ?? ''],
    queryFn: async () => {
      const { order } = await apiClient.get<{ order: SepayOrder }>(`/api/payments/sepay/orders/${encodeURIComponent(orderId ?? '')}`)
      if (order.status === 'paid') void qc.invalidateQueries({ queryKey: ['credits'] })
      return order
    },
    enabled: !!orderId,
    // Paid and expired orders stop polling; a hidden tab waits and refetches on focus
    refetchInterval: (query) => (!query.state.data || query.state.data.status === 'pending' ? SEPAY_POLL_MS : false),
    refetchIntervalInBackground: false,
  })
}
